// CTV-840: decide whether a station's logo needs knocking out to white on a
// dark banner.
//
// Channel 3000's logo is a navy mark on a transparent background. On paper and
// on their light storefront header it is fine; on the navy banner field it
// disappears, and nothing in config says so (logoInvert is for the header).
// So the banner looks at the logo itself: the average colour of its opaque
// pixels, checked for contrast against the field. Too little contrast and the
// logo is painted white (same filter the header uses for logoInvert). A
// colourful logo, or one on its own white box, clears the bar and is left as
// the station drew it.
//
// Reading pixels needs the image served with CORS, which both Supabase storage
// buckets do. A logo that cannot be read leaves the decision at "no knockout",
// the pre-existing behaviour.

import React from 'react';
import { contrastRatio, relativeLuminance } from './sheetColor';

/** Below this ratio against the field, the logo is knocked out to white. */
export const KNOCKOUT_CONTRAST = 2.5;

/** Pixels more transparent than this do not count toward the average. */
const OPAQUE_ALPHA = 128;

/**
 * A logo on its own opaque box (KWWL's lockup on white) cannot be knocked out:
 * the filter would paint the box, not the mark. Above this share of opaque
 * pixels the logo is left alone whatever its colour.
 */
const BOXED_OPAQUE_SHARE = 0.85;

/**
 * A knockout paints every opaque pixel one colour, so it only makes sense
 * for a flat mark. A lockup with a light box and dark type (KIMT's badge)
 * spreads its tones widely; painting it flat turns the box into a slab.
 * Above this standard deviation of pixel luminance the logo is left alone.
 */
const FLAT_TONE_SPREAD = 0.18;

/**
 * Feedback 2026-09-04: KEZI's lockup is a navy wordmark with a small light
 * badge. Its tones are spread (so the rule above leaves it alone) yet most
 * of it vanishes on the navy field. So before the spread rule, count the
 * pixels that would sink: when at least this share of the mark has too
 * little contrast with the field, it gets a plate whatever its spread.
 * KIMT's badge on the light look sits under the line (about half of it,
 * the white box, sinks; the navy type that matters stays readable).
 */
const SINKING_MAJORITY = 0.6;

export interface LogoSample {
  /** Average colour of the opaque pixels, or null when nothing is opaque. */
  mean: string | null;
  /** Share of pixels that are opaque, 0 to 1. */
  opaqueShare: number;
  /** Standard deviation of the opaque pixels' luminance, 0 (flat) to about 0.5. */
  toneSpread: number;
  /** Relative luminance (WCAG, 0 to 1) of each opaque pixel, for per-pixel contrast. */
  luminances: number[];
}

/** sRGB channel (0 to 255) to linear light, as WCAG defines it. */
function linear(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** The opaque pixels of an RGBA buffer: their average colour, coverage, and how varied their tones are. */
export function sampleLogo(data: Uint8ClampedArray): LogoSample {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  let lumSum = 0;
  let lumSq = 0;
  const total = Math.floor(data.length / 4);
  const luminances: number[] = [];
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < OPAQUE_ALPHA) continue;
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    const lum = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
    lumSum += lum;
    lumSq += lum * lum;
    luminances.push(
      0.2126 * linear(data[i]) + 0.7152 * linear(data[i + 1]) + 0.0722 * linear(data[i + 2]),
    );
    n += 1;
  }
  if (n === 0) return { mean: null, opaqueShare: 0, toneSpread: 0, luminances };
  const hex = (v: number) =>
    Math.round(v / n)
      .toString(16)
      .padStart(2, '0');
  const meanLum = lumSum / n;
  const variance = Math.max(0, lumSq / n - meanLum * meanLum);
  return {
    mean: `#${hex(r)}${hex(g)}${hex(b)}`,
    opaqueShare: n / total,
    toneSpread: Math.sqrt(variance),
    luminances,
  };
}

/** The share of the logo's opaque pixels that have too little contrast with `field`. */
export function sinkingShare(sample: LogoSample, field: string): number {
  if (sample.luminances.length === 0) return 0;
  const fieldLum = relativeLuminance(field);
  let sinking = 0;
  for (const lum of sample.luminances) {
    const hi = Math.max(lum, fieldLum);
    const lo = Math.min(lum, fieldLum);
    if ((hi + 0.05) / (lo + 0.05) < KNOCKOUT_CONTRAST) sinking += 1;
  }
  return sinking / sample.luminances.length;
}

/** `sampleLogo(...).mean`, kept for callers that only want the colour. */
export function meanOpaqueColor(data: Uint8ClampedArray): string | null {
  return sampleLogo(data).mean;
}

/** True when a logo like this would sink into `field` and a knockout would help. */
export function needsKnockout(sample: LogoSample | null, field: string): boolean {
  if (!sample || !sample.mean) return false;
  if (sample.opaqueShare > BOXED_OPAQUE_SHARE) return false;
  if (sinkingShare(sample, field) >= SINKING_MAJORITY) return true;
  if (sample.toneSpread > FLAT_TONE_SPREAD) return false;
  return contrastRatio(sample.mean, field) < KNOCKOUT_CONTRAST;
}

const SAMPLE = 48;
const sampleCache = new Map<string, Promise<LogoSample | null>>();

/** Load the logo and sample it on a small canvas. Cached per URL. */
export function logoSample(url: string): Promise<LogoSample | null> {
  const cached = sampleCache.get(url);
  if (cached) return cached;
  const p = new Promise<LogoSample | null>((resolve) => {
    if (typeof document === 'undefined') return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE;
        canvas.height = SAMPLE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
        resolve(sampleLogo(ctx.getImageData(0, 0, SAMPLE, SAMPLE).data));
      } catch {
        // Tainted canvas (no CORS) or a broken image: leave the logo alone.
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
  sampleCache.set(url, p);
  return p;
}

/**
 * Whether the logo at `url` should be knocked out on `field`. False until
 * the logo has been read, and false for anything that cannot be read.
 */
export function useLogoKnockout(url: string | null | undefined, field: string): boolean {
  const [sample, setSample] = React.useState<LogoSample | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    setSample(null);
    if (!url) return;
    logoSample(url).then((s) => {
      if (!cancelled) setSample(s);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return needsKnockout(sample, field);
}
